import { ReactNode, useState } from "react";
import { intl } from "LocaleWrapper";
import "../../styling/styling.scss";
import ApplicationDefinedStatusHistory from "view/statuses/components/changer/ApplicationDefinedStatusHistory";
import StatusChip from "view/statuses/components/changer/StatusChip";

export interface ApplicationDefinedStatus {
	name: string;
	color: string;
	languageKeyPrefix: string;
}

export interface ApplicationDefinedStatusChange<T extends ApplicationDefinedStatus,> {
	id: number;
	status: T;
	appliedBy: {
		displayName?: string | null;
		username?: string | null;
	};
	createdOn: number;
	notes?: string | null;
}

const unknownStatus: ApplicationDefinedStatus = {
	name: "UNKNOWN",
	color: "#333",
	languageKeyPrefix: "STATUS_",
};

/**
 * A convenience type that should be used in status changer dialogs.
 *  It extends the custom type with the {@code dialogProps} field returned by the {@code renderDialog} function.
 *
 *  @example
 *  ```tsx
 *  const MyStatusChangerDialog = ({
 *  	myProp,
 *  	dialogProps,
 *  }: ApplicationDefinedDialogProps<{ myProp: string }>) => (
 *  	<FormDialog {...dialogProps} form={myProp}>
 *  		(...)
 *  	</FormDialog>
 *  );
 *  ```
 */
export type ApplicationDefinedDialogProps<T extends object = object,> = T & {
	dialogProps: {
		open: boolean
		onClose: () => void,
		title: ReactNode,
	},
}

type ApplicationDefinedStatusChangerBaseProps<T extends ApplicationDefinedStatus,> = {
	currentStatus: T | null | undefined;
	disabled?: boolean;
	renderDialog: (dialogProps: ApplicationDefinedDialogProps["dialogProps"]) => ReactNode;
};

type ApplicationDefinedStatusChangerSimpleProps<T extends ApplicationDefinedStatus,> = {
	initStatusChanges?: never;
	historyTitle?: never;
	renderHistoryItem?: never;
} & ApplicationDefinedStatusChangerBaseProps<T>;

type ApplicationDefinedStatusChangerHistoryProps<T extends ApplicationDefinedStatus, U extends ApplicationDefinedStatusChange<T>,> = {
	initStatusChanges: () => Promise<U[]>;
	historyTitle?: ReactNode;
	renderHistoryItem: (statusChange: U) => ReactNode;
} & ApplicationDefinedStatusChangerBaseProps<T>;


const ApplicationDefinedStatusChanger = <T extends ApplicationDefinedStatus, U extends ApplicationDefinedStatusChange<T>,>({
	currentStatus,
	initStatusChanges,
	disabled,
	renderDialog,
	historyTitle = intl.formatMessage({ id: "STATUS_HISTORY" }),
	renderHistoryItem,
}: ApplicationDefinedStatusChangerSimpleProps<T> | ApplicationDefinedStatusChangerHistoryProps<T, U>) => {

	disabled ||= currentStatus === undefined;
	currentStatus ??= unknownStatus as T;

	const [ dialogOpen, setDialogOpen ] = useState(false);
	const [ drawerOpen, setDrawerOpen ] = useState(false);

	const showHistory = !!initStatusChanges && !!renderHistoryItem;

	return (
		<div
			onClick={(event) => event.stopPropagation()}
			onDoubleClick={(event) => event.stopPropagation()}
		>
			<StatusChip
				label={intl.formatMessage({ id: `${currentStatus.languageKeyPrefix}${currentStatus.name}` })}
				color={currentStatus.color}
				onClick={() => setDialogOpen(true)}
				disabled={disabled}
				onHistoryClick={showHistory ? () => setDrawerOpen(true) : undefined}
			/>

			{renderDialog({
				open: dialogOpen,
				onClose: () => setDialogOpen(false),
				title: intl.formatMessage({ id: `${currentStatus.languageKeyPrefix}${currentStatus.name}` }) + " - " + intl.formatMessage({ id: "CHANGE_STATUS" }),
			})}

			{showHistory && (
				<ApplicationDefinedStatusHistory
					open={drawerOpen}
					close={() => setDrawerOpen(false)}
					historyTitle={historyTitle}
					initStatusChanges={initStatusChanges}
					render={renderHistoryItem}
				/>
			)}
		</div>
	);
};

export default ApplicationDefinedStatusChanger;
